import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Popconfirm, Table, Tag } from 'antd';
import { ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { ROUTES } from '@/constants';
import { API } from '@/services/api.service';

const MODULES = ['customs', 'inputVat', 'vatRecording', 'fiscalBills'];

const dateTime = (v) => (v ? new Date(v).toLocaleString('sr-RS') : '—');

/**
 * Per module: switched on or not (company settings), what the morning job
 * found, when it last read, the history load, the last error. Admins start a
 * refresh. What the whole SEF channel costs is on the tech admin page.
 */
export default function StatusTab({ company, profile }) {
  const { t } = useTranslation('sefState');
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const refresh = useMutation({
    mutationFn: (history) => API.sefState.refresh(company, { history }),
    onSuccess: () => {
      message.success(t('status.refreshStarted'));
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['sefState', company] }), 15000);
    },
    onError: () => message.error(t('errors.refresh')),
  });

  const rows = MODULES.map((m) => ({ module: m, on: profile?.switches?.[m], ...(profile?.modules?.[m] || {}) }));
  const anyOn = rows.some((r) => r.on);

  return (
    <div className="flex flex-col gap-3">
      {!profile?.hasKey && <Alert type="warning" showIcon message={t('status.noKey')} />}
      {profile?.hasKey && !anyOn && (
        <Alert
          type="info"
          showIcon
          message={t('status.allOff')}
          action={
            profile?.canManage && (
              <Link to={`${ROUTES.admin}/${ROUTES.companies}/${company}`}>
                <Button shape="round" size="small" icon={<SettingOutlined />}>
                  {t('status.toSettings')}
                </Button>
              </Link>
            )
          }
        />
      )}

      <Card
        size="small"
        className="rounded-xl"
        title={t('status.title')}
        extra={
          profile?.canManage &&
          anyOn && (
            <div className="flex gap-2">
              <Button shape="round" icon={<ReloadOutlined />} loading={refresh.isPending} onClick={() => refresh.mutate(false)}>
                {t('status.refresh')}
              </Button>
              <Popconfirm title={t('status.historyConfirm')} onConfirm={() => refresh.mutate(true)}>
                <Button shape="round" loading={refresh.isPending}>
                  {t('status.refreshHistory')}
                </Button>
              </Popconfirm>
            </div>
          )
        }
      >
        <Table
          size="small"
          rowKey="module"
          pagination={false}
          dataSource={rows}
          scroll={{ x: 900 }}
          columns={[
            { title: t('status.columns.module'), dataIndex: 'module', width: 200, render: (m) => t(`tabs.${m}`) },
            {
              title: t('status.columns.switch'),
              dataIndex: 'on',
              width: 110,
              render: (on) => <Tag color={on ? 'green' : 'default'}>{on ? t('status.on') : t('status.off')}</Tag>,
            },
            {
              title: t('status.columns.hasData'),
              dataIndex: 'hasData',
              width: 130,
              render: (v, r) => (v == null ? '—' : v ? `${t('status.yes')} · ${r.count}` : t('status.no')),
            },
            { title: t('status.columns.lastSuccessAt'), dataIndex: 'lastSuccessAt', width: 170, render: dateTime },
            {
              title: t('status.columns.history'),
              key: 'history',
              width: 180,
              render: (_, r) =>
                r.historyDoneAt ? (
                  <Tag color="green">{t('status.historyDone')}</Tag>
                ) : r.historyFrom ? (
                  `${t('status.historyAt')} ${r.historyFrom}`
                ) : (
                  '—'
                ),
            },
            {
              title: t('status.columns.lastError'),
              dataIndex: 'lastError',
              render: (v) => (v ? <span className="text-xs text-red-600">{v}</span> : ''),
            },
          ]}
        />
        <p className="mt-2 text-xs opacity-60">{t('status.schedule')}</p>
      </Card>

    </div>
  );
}
